import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables, ChartOptions } from 'chart.js';
import { Box, useTheme, Typography, Paper, alpha } from '@mui/material';

Chart.register(...registerables);

interface MonthlyData {
  month: string;
  year: string;
  result: {
    totalShipment: number;
    totalWeight: number;
    totalRevenue: number;
    totalGrossProfit: number;
  };
}

interface BarChartProps {
  data: MonthlyData[] | undefined;
  title?: string;
}

const BarChart: React.FC<BarChartProps> = ({ data, title = 'Revenue & Profit Analysis' }) => {
  const theme = useTheme();

  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No data available for the selected period
        </Typography>
      </Paper>
    );
  }

  const months = data?.map(item => item.month);
  const revenueData = data?.map(item => item.result.totalRevenue);
  const grossProfitData = data?.map(item => item.result.totalGrossProfit);

  // Format numbers for tooltips (without currency)
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const barChartData = {
    labels: months,
    datasets: [
      {
        label: 'Revenue',
        data: revenueData,
        backgroundColor: theme.palette.primary.main,
        borderRadius: 8,
        barPercentage: 0.65,
        categoryPercentage: 0.8,
        hoverBackgroundColor: theme.palette.primary.dark,
      },
      {
        label: 'Gross Profit',
        data: grossProfitData,
        backgroundColor: theme.palette.secondary.main,
        borderRadius: 8,
        barPercentage: 0.65,
        categoryPercentage: 0.8,
        hoverBackgroundColor: theme.palette.secondary.dark,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 100,
    layout: {
      padding: {
        top: 20,
        right: 20,
        bottom: 10,
        left: 10,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.divider, 0.1),
        },
        ticks: {
          callback: value => formatNumber(value as number),
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
        },
        title: {
          display: true,
          text: 'Amount',
          color: theme.palette.text.secondary,
          font: {
            size: 12,
            weight: 'normal',
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        title: {
          display: true,
          text: 'Month',
          color: theme.palette.text.secondary,
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false, // Custom legend is used
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            const value = context.raw as number;
            return `${label}: ${formatNumber(value)}`;
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 4,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monthly revenue and gross profit comparison
        </Typography>
      </Box>

      {/* Custom Legend with Modern Design */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'end',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            />
            <Box>
              <Typography variant="body2" fontWeight={500} color="text.primary">
                Revenue
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.palette.secondary.main,
                boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.2)}`,
              }}
            />
            <Box>
              <Typography variant="body2" fontWeight={500} color="text.primary">
                Gross Profit
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Chart Container with Width Protection */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minWidth: 0, // Prevents overflow
          overflowX: 'auto',
          overflowY: 'visible',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: { xs: '350px', sm: '450px', md: '500px' },
            width: '100%',
            minWidth: { xs: '300px', sm: 'auto' },
          }}
        >
          <Bar data={barChartData} options={options} />
        </Box>
      </Box>
    </Paper>
  );
};

export default BarChart;
